import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Switch,
  TextField,
  Grid,
  Tooltip,
  tableCellClasses,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { toast } from "react-toastify";
import { useConfirm } from "material-ui-confirm";
import { showLoader } from "../../../../Redux/Slices/loaderSlice";
import {
  get_role_access,
  get_sidebar_config,
  update_page_settings,
  add_new_page,
  delete_page,
  getAvailableAccess,
} from "../../../../Redux/Actions/action";
import ErrorHandler, { checkAccess } from "../../../../Utils/handler";
import ButtonContained from "../../../Common/ButtonContained";
import ButtonOutlined from "../../../Common/ButtonOutlined";
import CustomDialog from "../../../Common/CustomDialog";
import CommonTextField from "../../../Common/CommonTextField";

// Icon mapping for preview
const ICON_MAP = {
  dashboard: "dashboardHigh.svg",
  addMember: "addMemberr.svg",
  website: "elements.svg",
  memberRequest: "memberRequest.svg",
  documents: "documents.svg",
  claims: "claims.svg",
  memberSearch: "membersearch.svg",
  viewMember: "membersearch.svg",
  paymentRequest: "allTransaction.svg",
  notifications: "notifications.svg",
  helpDesk: "helpDesk.svg",
  reports: "reports.svg",
  settings: "settings.svg",
  transactionHistory: "transactionHistory.svg",
  allTransaction: "allTransaction.svg",
  default: "defaultIcon.svg",
};

// Fallback mappings for pages without icon/section set
const PAGE_NAME_TO_ICON = {
  "Dashboard": "dashboard",
  "Add Member": "addMember",
  "Website": "website",
  "Member Requests": "memberRequest",
  "Documents": "documents",
  "Document Status Wise": "documents",
  "Claims": "claims",
  "Member Search": "memberSearch",
  "View Member": "viewMember",
  "Payment Request": "paymentRequest",
  "Notifications": "notifications",
  "Help Desk": "helpDesk",
  "Reports": "reports",
  "Settings": "settings",
  "Transaction History": "transactionHistory",
  "All Transaction": "allTransaction",
};

const PAGE_NAME_TO_SECTION = {
  "Dashboard": "MAIN MENU",
  "Add Member": "MAIN MENU",
  "Website": "MAIN MENU",
  "Member Requests": "CLAIMS",
  "Documents": "CLAIMS",
  "Document Status Wise": "CLAIMS",
  "Claims": "CLAIMS",
  "Member Search": "CLAIMS",
  "View Member": "CLAIMS",
  "Payment Request": "CLAIMS",
};

// Helper to get icon with fallback
const getPageIcon = (page) => {
  return page.icon || PAGE_NAME_TO_ICON[page.pageName] || "default";
};

// Helper to get section with fallback
const getPageSection = (page) => {
  return page.section || PAGE_NAME_TO_SECTION[page.pageName] || "OTHER";
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme?.palette?.primary?.main,
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    padding: "12px 16px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "10px 16px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#fafafa",
  },
  "&:hover": {
    backgroundColor: "#f1f5f9",
  },
  "&:last-of-type td, &:last-of-type th": {
    borderBottom: "none",
  },
}));

// Default sections and icons (fallback if API doesn't return them)
const DEFAULT_SECTIONS = ["MAIN MENU", "CLAIMS", "OTHER"];
const DEFAULT_ICONS = [
  "dashboard", "addMember", "website", "memberRequest", "documents",
  "claims", "memberSearch", "viewMember", "paymentRequest", "notifications",
  "helpDesk", "reports", "settings", "transactionHistory", "allTransaction", "default"
];

const PageManagement = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const confirmDialogue = useConfirm();

  // State
  const [pages, setPages] = useState([]);
  const [sidebarConfig, setSidebarConfig] = useState({ 
    sections: DEFAULT_SECTIONS, 
    icons: DEFAULT_ICONS 
  });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [searchText, setSearchText] = useState("");
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openSectionDialog, setOpenSectionDialog] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [newPageData, setNewPageData] = useState({
    path: "",
    pageName: "",
    icon: "default",
    section: "OTHER",
    displayOrder: 0,
  });
  const [errors, setErrors] = useState({});

  const dataAccess = useSelector(
    (value) => value?.allAvailableAccess?.AllAvailableAccessList
  );

  // Fetch data
  const fetchPages = async () => {
    dispatch(showLoader(true));
    try {
      const result = await dispatch(get_role_access());
      const error = ErrorHandler(result);
      if (error) {
        setPages(result?.payload?.data?.data || []);
      }
    } catch (error) {
      toast.error("Failed to fetch pages");
    } finally {
      dispatch(showLoader(false));
    }
  };

  const fetchSidebarConfig = async () => {
    try {
      const result = await dispatch(get_sidebar_config());
      const error = ErrorHandler(result);
      if (error && result?.payload?.data) {
        setSidebarConfig({
          sections: result?.payload?.data?.sections?.length > 0 
            ? result.payload.data.sections 
            : DEFAULT_SECTIONS,
          icons: result?.payload?.data?.icons?.length > 0 
            ? result.payload.data.icons 
            : DEFAULT_ICONS,
        });
      }
    } catch (error) {
      console.error("Failed to fetch sidebar config");
      // Keep default values on error
    }
  };

  useEffect(() => {
    fetchPages();
    fetchSidebarConfig();
  }, []); // eslint-disable-line

  // Handle edit
  const handleEdit = (page) => {
    setEditingId(page._id);
    setEditData({
      icon: getPageIcon(page),
      section: getPageSection(page),
      displayOrder: page.displayOrder || 0,
      isVisible: page.isVisible !== false,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSaveEdit = async (pageId) => {
    dispatch(showLoader(true));
    try {
      const result = await dispatch(
        update_page_settings({
          pageId,
          ...editData,
        })
      );
      const error = ErrorHandler(result);
      if (error) {
        toast.success("Page settings updated successfully");
        setEditingId(null);
        setEditData({});
        fetchPages();
        // Refresh available access to update sidebar
        dispatch(getAvailableAccess());
      }
    } catch (error) {
      toast.error("Failed to update page settings");
    } finally {
      dispatch(showLoader(false));
    }
  };

  // Handle add new page
  const handleAddPage = async () => {
    const newErrors = {};
    if (!newPageData.path) newErrors.path = "Path is required";
    if (!newPageData.pageName) newErrors.pageName = "Page name is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    dispatch(showLoader(true));
    try {
      const result = await dispatch(add_new_page(newPageData));
      const error = ErrorHandler(result);
      if (error) {
        toast.success("Page added successfully");
        setOpenAddDialog(false);
        setNewPageData({
          path: "",
          pageName: "",
          icon: "default",
          section: "OTHER",
          displayOrder: 0,
        });
        setErrors({});
        fetchPages();
      }
    } catch (error) {
      toast.error("Failed to add page");
    } finally {
      dispatch(showLoader(false));
    }
  };

  // Handle delete
  const handleDelete = async (pageId, pageName) => {
    confirmDialogue({
      title: `Delete "${pageName}"?`,
      description: "This will permanently remove this page from the sidebar. This action cannot be undone.",
      confirmationText: (
        <Typography sx={{ fontSize: "14px" }}>Yes, Delete</Typography>
      ),
      cancellationText: (
        <Typography sx={{ fontSize: "14px" }}>Cancel</Typography>
      ),
      titleProps: { sx: { fontSize: "18px", fontWeight: 500, color: "#dc2626" } },
    })
      .then(async () => {
        dispatch(showLoader(true));
        try {
          const result = await dispatch(delete_page({ pageId }));
          const error = ErrorHandler(result);
          if (error) {
            toast.success("Page deleted successfully");
            fetchPages();
            dispatch(getAvailableAccess());
          }
        } catch (error) {
          toast.error("Failed to delete page");
        } finally {
          dispatch(showLoader(false));
        }
      })
      .catch(() => {
        // User cancelled
      });
  };

  // Filter pages by search
  const filteredPages = pages.filter(
    (page) =>
      page.pageName?.toLowerCase().includes(searchText.toLowerCase()) ||
      page.path?.toLowerCase().includes(searchText.toLowerCase())
  );

  // Group pages by section for display (with fallback)
  const groupedPages = filteredPages.reduce((acc, page) => {
    const section = getPageSection(page);
    if (!acc[section]) acc[section] = [];
    acc[section].push(page);
    return acc;
  }, {});

  // Sort pages within each section by displayOrder
  Object.keys(groupedPages).forEach((section) => {
    groupedPages[section].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  });

  return (
    <Box sx={{ backgroundColor: "#f8f8f8", padding: "18px", borderRadius: "8px" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "22px", fontWeight: 600, color: "#1e293b" }}>
            Page Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure sidebar pages, icons, sections, and display order
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Search pages..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 250,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#fff",
              },
            }}
          />
          <Tooltip title="Manage Sections">
            <IconButton
              onClick={() => setOpenSectionDialog(true)}
              sx={{
                backgroundColor: "#f1f5f9",
                "&:hover": { backgroundColor: "#e2e8f0" },
              }}
            >
              <SettingsIcon sx={{ color: "#64748b" }} />
            </IconButton>
          </Tooltip>
          {checkAccess(pathname, dataAccess, "AddPage") && (
            <ButtonContained
              text="+ Add Page"
              height="38px"
              borderRadius="8px"
              onClick={() => setOpenAddDialog(true)}
              sx={{
                background: "linear-gradient(135deg, #3E4094 0%, #6366f1 100%)",
                boxShadow: "0 2px 4px rgba(62, 64, 148, 0.25)",
              }}
            />
          )}
        </Box>
      </Box>

      {/* Pages Table */}
      <Paper elevation={0} sx={{ borderRadius: "12px", overflow: "hidden" }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>Icon</StyledTableCell>
              <StyledTableCell>Page Name</StyledTableCell>
              <StyledTableCell>Path</StyledTableCell>
              <StyledTableCell>Section</StyledTableCell>
              <StyledTableCell align="center">Order</StyledTableCell>
              <StyledTableCell align="center">Visible</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sidebarConfig.sections.map((section) => (
              <React.Fragment key={section}>
                {groupedPages[section]?.length > 0 && (
                  <>
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        sx={{
                          backgroundColor: "#e2e8f0",
                          fontWeight: 600,
                          fontSize: "13px",
                          color: "#475569",
                          py: 1,
                        }}
                      >
                        {section}
                      </TableCell>
                    </TableRow>
                    {groupedPages[section]?.map((page) => (
                      <StyledTableRow key={page._id}>
                        <StyledTableCell>
                          {editingId === page._id ? (
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                              <Select
                                value={editData.icon || "default"}
                                onChange={(e) =>
                                  setEditData({ ...editData, icon: e.target.value })
                                }
                              >
                                {sidebarConfig.icons.map((icon) => (
                                  <MenuItem key={icon} value={icon}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      <img
                                        src={`/images/${ICON_MAP[icon] || "defaultIcon.svg"}`}
                                        alt={icon}
                                        height="20px"
                                        width="20px"
                                        onError={(e) => { e.target.src = '/images/defaultIcon.svg'; }}
                                      />
                                      <span>{icon}</span>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <img
                              src={`/images/${ICON_MAP[getPageIcon(page)] || "defaultIcon.svg"}`}
                              alt={page.pageName}
                              height="24px"
                              width="24px"
                              onError={(e) => { e.target.src = '/images/defaultIcon.svg'; }}
                            />
                          )}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography fontWeight={500}>{page.pageName}</Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Chip
                            label={page.path}
                            size="small"
                            sx={{
                              backgroundColor: "#e0e7ff",
                              color: "#3730a3",
                              fontFamily: "monospace",
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          {editingId === page._id ? (
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={editData.section || "OTHER"}
                                onChange={(e) =>
                                  setEditData({ ...editData, section: e.target.value })
                                }
                              >
                                {sidebarConfig.sections.map((sec) => (
                                  <MenuItem key={sec} value={sec}>
                                    {sec}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip
                              label={getPageSection(page)}
                              size="small"
                              sx={{
                                backgroundColor:
                                  getPageSection(page) === "MAIN MENU"
                                    ? "#dcfce7"
                                    : getPageSection(page) === "CLAIMS"
                                    ? "#fef3c7"
                                    : "#f1f5f9",
                                color:
                                  getPageSection(page) === "MAIN MENU"
                                    ? "#166534"
                                    : getPageSection(page) === "CLAIMS"
                                    ? "#92400e"
                                    : "#475569",
                              }}
                            />
                          )}
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          {editingId === page._id ? (
                            <TextField
                              type="number"
                              size="small"
                              value={editData.displayOrder || 0}
                              onChange={(e) =>
                                setEditData({
                                  ...editData,
                                  displayOrder: parseInt(e.target.value) || 0,
                                })
                              }
                              sx={{ width: 70 }}
                            />
                          ) : (
                            <Typography>{page.displayOrder || 0}</Typography>
                          )}
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          {editingId === page._id ? (
                            <Switch
                              checked={editData.isVisible !== false}
                              onChange={(e) =>
                                setEditData({ ...editData, isVisible: e.target.checked })
                              }
                              color="primary"
                            />
                          ) : (
                            <Chip
                              label={page.isVisible !== false ? "Yes" : "No"}
                              size="small"
                              sx={{
                                backgroundColor:
                                  page.isVisible !== false ? "#dcfce7" : "#fee2e2",
                                color: page.isVisible !== false ? "#166534" : "#991b1b",
                              }}
                            />
                          )}
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          {editingId === page._id ? (
                            <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                              <Tooltip title="Save">
                                <IconButton
                                  size="small"
                                  onClick={() => handleSaveEdit(page._id)}
                                  sx={{ color: "#16a34a" }}
                                >
                                  <SaveIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton
                                  size="small"
                                  onClick={handleCancelEdit}
                                  sx={{ color: "#dc2626" }}
                                >
                                  <CancelIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                              {checkAccess(pathname, dataAccess, "EditPage") && (
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEdit(page)}
                                    sx={{ color: "#3E4094" }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {checkAccess(pathname, dataAccess, "DeletePage") && (
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDelete(page._id, page.pageName)}
                                    sx={{ color: "#dc2626" }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </>
                )}
              </React.Fragment>
            ))}
            {filteredPages.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No pages found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Add Page Dialog */}
      <CustomDialog
        isOpen={openAddDialog}
        handleClose={() => {
          setOpenAddDialog(false);
          setNewPageData({
            path: "",
            pageName: "",
            icon: "default",
            section: "OTHER",
            displayOrder: 0,
          });
          setErrors({});
        }}
        title="Add New Page"
        maxWidth="sm"
      >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <CommonTextField
                label="Page Name *"
                placeholder="Enter page name"
                value={newPageData.pageName}
                onChange={(e) =>
                  setNewPageData({ ...newPageData, pageName: e.target.value })
                }
                error={errors.pageName}
              />
            </Grid>
            <Grid item xs={12}>
              <CommonTextField
                label="Path *"
                placeholder="Enter path (e.g., /dashboard)"
                value={newPageData.path}
                onChange={(e) =>
                  setNewPageData({ ...newPageData, path: e.target.value })
                }
                error={errors.path}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Icon
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={newPageData.icon}
                  onChange={(e) =>
                    setNewPageData({ ...newPageData, icon: e.target.value })
                  }
                >
                  {sidebarConfig.icons.map((icon) => (
                    <MenuItem key={icon} value={icon}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <img
                          src={`/images/${ICON_MAP[icon] || "defaultIcon.svg"}`}
                          alt={icon}
                          height="20px"
                          width="20px"
                          onError={(e) => { e.target.src = '/images/defaultIcon.svg'; }}
                        />
                        <span>{icon}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Section
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={newPageData.section}
                  onChange={(e) =>
                    setNewPageData({ ...newPageData, section: e.target.value })
                  }
                >
                  {sidebarConfig.sections.map((sec) => (
                    <MenuItem key={sec} value={sec}>
                      {sec}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <CommonTextField
                label="Display Order"
                type="number"
                placeholder="Enter display order"
                value={newPageData.displayOrder}
                onChange={(e) =>
                  setNewPageData({
                    ...newPageData,
                    displayOrder: parseInt(e.target.value) || 0,
                  })
                }
              />
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 3 }}>
            <ButtonOutlined
              text="Cancel"
              onClick={() => {
                setOpenAddDialog(false);
                setNewPageData({
                  path: "",
                  pageName: "",
                  icon: "default",
                  section: "OTHER",
                  displayOrder: 0,
                });
                setErrors({});
              }}
            />
            <ButtonContained text="Add Page" onClick={handleAddPage} />
          </Box>
      </CustomDialog>

      {/* Section Management Dialog */}
      <CustomDialog
        isOpen={openSectionDialog}
        handleClose={() => {
          setOpenSectionDialog(false);
          setNewSectionName("");
        }}
        title="Manage Sections"
        maxWidth="sm"
      >
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sections are used to group pages in the sidebar. Add or remove sections as needed.
          </Typography>
          
          {/* Current Sections */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Current Sections
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {sidebarConfig.sections.map((section) => (
                <Chip
                  key={section}
                  label={section}
                  onDelete={
                    section !== "OTHER" 
                      ? () => {
                          confirmDialogue({
                            title: `Remove "${section}" section?`,
                            description: "Pages in this section will be moved to OTHER.",
                            confirmationText: "Remove",
                            cancellationText: "Cancel",
                          }).then(() => {
                            setSidebarConfig(prev => ({
                              ...prev,
                              sections: prev.sections.filter(s => s !== section)
                            }));
                            toast.success(`Section "${section}" removed`);
                          }).catch(() => {});
                        }
                      : undefined
                  }
                  sx={{
                    backgroundColor:
                      section === "MAIN MENU"
                        ? "#dcfce7"
                        : section === "CLAIMS"
                        ? "#fef3c7"
                        : "#f1f5f9",
                    color:
                      section === "MAIN MENU"
                        ? "#166534"
                        : section === "CLAIMS"
                        ? "#92400e"
                        : "#475569",
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Add New Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Add New Section
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                size="small"
                placeholder="Enter section name (e.g., REPORTS)"
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value.toUpperCase())}
                sx={{ flex: 1 }}
              />
              <ButtonContained
                text="Add"
                onClick={() => {
                  if (!newSectionName.trim()) {
                    toast.error("Section name is required");
                    return;
                  }
                  if (sidebarConfig.sections.includes(newSectionName.trim())) {
                    toast.error("Section already exists");
                    return;
                  }
                  setSidebarConfig(prev => ({
                    ...prev,
                    sections: [...prev.sections.filter(s => s !== "OTHER"), newSectionName.trim(), "OTHER"]
                  }));
                  setNewSectionName("");
                  toast.success(`Section "${newSectionName.trim()}" added`);
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Note: "OTHER" section is always at the end and cannot be removed.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <ButtonOutlined
            text="Close"
            onClick={() => {
              setOpenSectionDialog(false);
              setNewSectionName("");
            }}
          />
        </Box>
      </CustomDialog>
    </Box>
  );
};

export default PageManagement;
